import React, { useEffect, useMemo } from 'react';
import { isToday, parseISO, format } from 'date-fns';
import { useRecallReports, useRecallReportDetail } from '@/hooks/recall-report';
import { useRecallReportStore } from '@/stores';
import {
  WaitingState,
  HistoricalBanner,
} from '@/features/recall-report/components';
import { RecallReportView } from '@/features/counterparty-reports/components/RecallReportView';
import { TourSentinel } from '@/features/onboarding';

interface RecallReportTabProps {
  searchQuery?: string;
}

export const RecallReportTab: React.FC<RecallReportTabProps> = ({ searchQuery }) => {
  const counterparty = useRecallReportStore((state) => state.activeCounterparty);
  const selectedDate = useRecallReportStore((state) => state.selectedDate);
  const setSelectedDate = useRecallReportStore((state) => state.setSelectedDate);
  const selectedReportId = useRecallReportStore((state) => state.selectedReportId);
  const setSelectedReportId = useRecallReportStore((state) => state.setSelectedReportId);

  // Primary data: reports filtered by counterparty on the server
  const { data: reports = [] } = useRecallReports({ counterparty });

  const todayReport = useMemo(
    () => reports.find((r) => isToday(parseISO(r.created_at))),
    [reports],
  );

  const hasTodayReport = !!todayReport;

  // Poll when waiting for today's report (same query key -- TanStack Query merges observers)
  useRecallReports({
    counterparty,
    refetchInterval: !selectedDate && !hasTodayReport ? 5 * 60 * 1000 : false,
  });

  const dateReport = useMemo(() => {
    if (!selectedDate) return null;
    return reports.find(
      (r) => format(parseISO(r.created_at), 'yyyy-MM-dd') === selectedDate,
    );
  }, [reports, selectedDate]);

  const activeReport = selectedDate ? dateReport : todayReport;

  // Auto-select report when active report changes
  useEffect(() => {
    if (activeReport) {
      setSelectedReportId(activeReport.id);
    } else {
      setSelectedReportId(null);
    }
  }, [activeReport, setSelectedReportId]);

  const {
    data: reportDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useRecallReportDetail(selectedReportId);

  const showWaitingState = !selectedDate && !hasTodayReport;

  return (
    <div className="relative flex h-full flex-col">
      <TourSentinel name="cpty-recall-overview" />
      {/* Report content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {showWaitingState ? (
          <WaitingState />
        ) : (
          <>
            {selectedDate && (
              <HistoricalBanner
                date={format(parseISO(selectedDate), 'dd MMM yyyy')}
                onBackToToday={() => setSelectedDate(null)}
              />
            )}
            <RecallReportView
              report={reportDetail}
              isLoading={isDetailLoading}
              error={detailError}
              searchQuery={searchQuery}
            />
          </>
        )}
      </div>
    </div>
  );
};
